import React, { useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, SwitchCamera, Volume2, Volume1, Maximize2, Minimize2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { startRingback, stopRingback, startCallRing, stopCallRing } from "@/lib/sound";
import { getInitials, avatarColor } from "@/lib/avatar";
import { logCall } from "@/components/chat/CallsPage";
import { toast } from "sonner";

// Best-effort 1:1 WebRTC call over the app's WebSocket signaling.
export default function CallOverlay({ chat, video, onClose, sendWs, outgoing, incoming }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const peerId = outgoing ? chat?.peer?.id : incoming?.from;
  const peerName = outgoing ? chat?.title : incoming?.from_name;
  const [status, setStatus] = useState(outgoing ? "Calling…" : "Incoming call");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(video);
  const [answered, setAnswered] = useState(false);
  const [poorNet, setPoorNet] = useState(false);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(!!video); // video defaults to loudspeaker; voice to earpiece
  const [hasHeadset, setHasHeadset] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [showCtrl, setShowCtrl] = useState(true);
  const [micDenied, setMicDenied] = useState(false); // iOS Safari: mic permission explicitly denied
  const ctrlTimer = useRef(null);
  const pcRef = useRef(null);
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const outputsRef = useRef([]);
  const streamRef = useRef(null);
  const qualityRef = useRef(0);
  const answeredRef = useRef(false);
  const pendingIce = useRef([]);
  const facingRef = useRef("user");
  const loggedRef = useRef(false);
  const connectedAtRef = useRef(null);
  const failedRef = useRef(false);
  const reconnectingRef = useRef(false);
  const reconnectDeadline = useRef(null);
  const reconnectRetry = useRef(null);
  const RECONNECT_GRACE_MS = 25000;

  const addIce = async (pc, candidate) => {
    if (pc.remoteDescription && pc.remoteDescription.type) {
      try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) {}
    } else {
      pendingIce.current.push(candidate);
    }
  };
  const flushIce = async (pc) => {
    const list = pendingIce.current;
    pendingIce.current = [];
    for (const c of list) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) {} }
  };

  const cleanup = () => {
    clearReconnect();
    try { pcRef.current?.close(); } catch (e) {}
    streamRef.current?.getTracks().forEach((t) => t.stop());
    try { audioCtxRef.current?.close(); audioCtxRef.current = null; } catch (e) {}
  };

  const clearReconnect = () => {
    clearTimeout(reconnectDeadline.current);
    clearInterval(reconnectRetry.current);
    reconnectDeadline.current = null;
    reconnectRetry.current = null;
  };

  // Caller re-negotiates the media path with a fresh ICE gather to survive a network switch/blip.
  const tryReconnect = async () => {
    const pc = pcRef.current;
    if (!pc || !outgoing) return;
    try {
      if (pc.restartIce) pc.restartIce();
      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);
      sendWs({ type: "rtc_offer", to: peerId, sdp: offer, video, reconnect: true });
    } catch (e) {}
  };

  const endDueToDrop = () => {
    if (!reconnectingRef.current) return;
    clearReconnect();
    reconnectingRef.current = false;
    toast("Call ended — connection lost");
    cleanup();
    onClose();
  };

  const beginReconnect = () => {
    if (reconnectingRef.current || !connectedAtRef.current) return; // only after a real connection
    reconnectingRef.current = true;
    setReconnecting(true);
    setStatus("Reconnecting…");
    reconnectDeadline.current = setTimeout(endDueToDrop, RECONNECT_GRACE_MS);
    if (outgoing) {
      tryReconnect();
      reconnectRetry.current = setInterval(tryReconnect, 4000); // keep retrying within the grace window
    }
  };

  const endReconnect = () => {
    if (!reconnectingRef.current) return;
    reconnectingRef.current = false;
    setReconnecting(false);
    clearReconnect();
  };

  // iOS Safari plays remote audio only if the <audio> element is started inside a
  // user gesture (the Call/Accept tap). Prime it here and resume the AudioContext so
  // the callee actually hears the other side (fixes one-way audio on iPhone webapp).
  const unlockRemoteAudio = () => {
    const el = remoteAudioRef.current;
    if (el) {
      el.muted = false; el.volume = 1;
      try { const p = el.play(); if (p && p.catch) p.catch(() => {}); } catch (e) {}
    }
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        if (!audioCtxRef.current) audioCtxRef.current = new AC();
        if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume().catch(() => {});
      }
    } catch (e) {}
  };

  // Only block the call if the mic was explicitly DENIED; 'prompt'/'granted' proceed
  // (getUserMedia will surface the native prompt when needed).
  const micAvailable = async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const st = await navigator.permissions.query({ name: "microphone" });
        if (st.state === "denied") { setMicDenied(true); return false; }
        setMicDenied(false);
      }
    } catch (e) { /* Permissions API unsupported (older iOS) — let getUserMedia decide */ }
    return true;
  };

  const setupPc = async () => {
    const { data } = await api.get("/rtc/ice");
    const pc = new RTCPeerConnection({ iceServers: data.iceServers });
    pcRef.current = pc;
    pc.onicecandidate = (e) => { if (e.candidate) sendWs({ type: "rtc_ice", to: peerId, candidate: e.candidate }); };
    pc.ontrack = (e) => {
      const [rs] = e.streams;
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = rs; // audio routed here (sink-controlled)
        remoteAudioRef.current.muted = false;
        const pr = remoteAudioRef.current.play(); // iOS: explicit play now that a track exists
        if (pr && pr.catch) pr.catch(() => {});
      }
      if (remoteRef.current) remoteRef.current.srcObject = rs;           // video (muted; audio via audio el)
      setStatus("Connected"); setConnected(true); if (!connectedAtRef.current) connectedAtRef.current = Date.now();
      applyOutput();
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === "failed" && !connectedAtRef.current) failedRef.current = true;
      if (["disconnected", "failed"].includes(st)) beginReconnect();
      if (st === "connected") {
        endReconnect();
        setStatus("Connected"); setConnected(true);
        if (!connectedAtRef.current) connectedAtRef.current = Date.now();
      }
    };
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      video: video ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } } : false,
    });
    streamRef.current = stream;
    const vt0 = stream.getVideoTracks()[0];
    if (vt0) { try { vt0.contentHint = "motion"; } catch (e) {} }
    if (localRef.current) localRef.current.srcObject = stream;
    stream.getTracks().forEach((tr) => pc.addTrack(tr, stream));
    applyQuality(qualityRef.current);
    return pc;
  };

  const startOutgoing = async () => {
    try {
      unlockRemoteAudio();
      if (!(await micAvailable())) { toast.error("Microphone permission is blocked. Enable it in Settings › Safari."); return; }
      const pc = await setupPc();
      sendWs({ type: "rtc_call", to: peerId, video });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendWs({ type: "rtc_offer", to: peerId, sdp: offer, video });
    } catch (e) { toast.error("Camera/microphone unavailable"); onClose(); }
  };

  const answer = async () => {
    if (answeredRef.current) return; // guard against double-answer (re-tap / re-fired event)
    answeredRef.current = true;
    try {
      unlockRemoteAudio();
      if (!(await micAvailable())) { toast.error("Microphone permission is blocked. Enable it in Settings › Safari."); answeredRef.current = false; return; }
      setAnswered(true);
      const pc = await setupPc();
      if (incoming?.sdp) {
        await pc.setRemoteDescription(new RTCSessionDescription(incoming.sdp));
        await flushIce(pc);
        const ans = await pc.createAnswer();
        await pc.setLocalDescription(ans);
        sendWs({ type: "rtc_answer", to: peerId, sdp: ans });
      }
      setStatus("Connected");
    } catch (e) { toast.error("Could not start call"); hangup(); }
  };

  const hangup = () => { sendWs({ type: "rtc_end", to: peerId }); cleanup(); onClose(); };

  // Auto-answer when launched from a push "Accept": answer as soon as the (re)offer arrives.
  useEffect(() => {
    if (incoming && incoming.autoAnswer && incoming.sdp && !answeredRef.current) answer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming && incoming.sdp]);

  // --- Audio output routing (earpiece / loudspeaker / auto-headset) ---
  const HEADSET_KW = ["headphone", "headset", "bluetooth", "wired", "airpod", "earbud", "hands-free", "hearing"];
  const pickOutput = () => {
    const outs = outputsRef.current || [];
    const byKw = (kws) => outs.find((o) => kws.some((k) => (o.label || "").toLowerCase().includes(k)));
    const def = outs.find((o) => o.deviceId === "default") || outs[0];
    const headset = byKw(HEADSET_KW);
    if (headset) return headset;
    if (speakerOn) return byKw(["speaker"]) || def;
    return byKw(["earpiece", "handset", "receiver"]) || def; // earpiece by default
  };
  const applyOutput = async () => {
    const el = remoteAudioRef.current;
    const target = pickOutput();
    if (el && target && typeof el.setSinkId === "function") {
      try { await el.setSinkId(target.deviceId); } catch (e) {}
    }
  };
  const refreshOutputs = async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      outputsRef.current = devs.filter((d) => d.kind === "audiooutput");
      const hs = outputsRef.current.some((o) => HEADSET_KW.some((k) => (o.label || "").toLowerCase().includes(k)));
      setHasHeadset(hs);
      await applyOutput();
    } catch (e) {}
  };

  useEffect(() => {
    if (outgoing) startOutgoing();
    const onRtc = async (ev) => {
      const m = ev.detail;
      if (m.from !== peerId) return;
      const pc = pcRef.current;
      if (m.type === "rtc_answer" && pc) {
        // Only apply the answer if we're actually waiting for one; ignore duplicate/late
        // answers to avoid "setRemoteDescription ... Called in wrong state: stable".
        if (pc.signalingState === "have-local-offer") {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(m.sdp));
            await flushIce(pc);
            setStatus("Connected"); setConnected(true);
          } catch (e) { /* ignore stale answer */ }
        }
      }
      else if (m.type === "rtc_ice" && pc) { await addIce(pc, m.candidate); }
      else if (m.type === "rtc_offer" && pc && m.reconnect) {
        // ICE-restart re-offer from the caller after a network blip — answer it to resume.
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(m.sdp));
          await flushIce(pc);
          const ans = await pc.createAnswer();
          await pc.setLocalDescription(ans);
          sendWs({ type: "rtc_answer", to: peerId, sdp: ans });
        } catch (e) {}
      }
      else if (m.type === "rtc_end" || m.type === "rtc_reject") { toast(m.type === "rtc_reject" ? "Call declined" : "Call ended"); cleanup(); onClose(); }
      else if (m.type === "rtc_ready" && outgoing && pcRef.current) {
        // Callee just launched from a push notification and is ready — (re)send the offer.
        const st = pcRef.current.signalingState;
        if (st === "stable" || st === "have-local-offer") {
          try {
            const offer = await pcRef.current.createOffer({ iceRestart: true });
            await pcRef.current.setLocalDescription(offer);
            sendWs({ type: "rtc_offer", to: peerId, sdp: offer, video });
          } catch (e) { /* ignore */ }
        }
      }
    };
    window.addEventListener("rc-rtc", onRtc);
    return () => {
      window.removeEventListener("rc-rtc", onRtc);
      cleanup();
      if (!loggedRef.current) {
        loggedRef.current = true;
        const answeredCall = !!connectedAtRef.current;
        const duration = answeredCall ? Math.max(1, Math.round((Date.now() - connectedAtRef.current) / 1000)) : 0;
        logCall({
          name: peerName,
          video: !!video,
          outgoing: !!outgoing,
          missed: !!incoming && !answeredRef.current,
          peerId,
          chatId: outgoing ? chat?.id : undefined,
        });
        // Caller persists a WhatsApp-style call log message so both sides see it in the thread.
        if (outgoing && chat?.id) {
          const status = answeredCall ? "completed" : (failedRef.current ? "failed" : "no_answer");
          api.post("/messages", {
            chat_id: chat.id, type: "call",
            call_meta: { media: video ? "video" : "audio", status, duration },
          }).catch(() => {});
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-route audio when the loudspeaker toggle changes.
  useEffect(() => { applyOutput(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [speakerOn]);
  // Enumerate outputs after connect (labels need mic permission) and auto-switch when a headset is plugged/removed.
  useEffect(() => {
    refreshOutputs();
    const md = navigator.mediaDevices;
    if (md && md.addEventListener) {
      md.addEventListener("devicechange", refreshOutputs);
      return () => md.removeEventListener("devicechange", refreshOutputs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  const toggleMic = () => { const t = streamRef.current?.getAudioTracks()[0]; if (t) { t.enabled = !t.enabled; setMicOn(t.enabled); } };
  const toggleCam = () => { const t = streamRef.current?.getVideoTracks()[0]; if (t) { t.enabled = !t.enabled; setCamOn(t.enabled); } };

  // Flip between front/back camera mid-call.
  const switchCamera = async () => {
    const pc = pcRef.current;
    if (!pc || !video) return;
    const next = facingRef.current === "user" ? "environment" : "user";
    try {
      const ns = await navigator.mediaDevices.getUserMedia({ video: { facingMode: next, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } }, audio: false });
      const newTrack = ns.getVideoTracks()[0];
      if (!newTrack) return;
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
      if (sender) await sender.replaceTrack(newTrack);
      const old = streamRef.current?.getVideoTracks()[0];
      if (old) { streamRef.current.removeTrack(old); old.stop(); }
      streamRef.current?.addTrack(newTrack);
      if (localRef.current) localRef.current.srcObject = streamRef.current;
      facingRef.current = next;
      setCamOn(newTrack.enabled);
    } catch (e) { toast.error("Cannot switch camera"); }
  };

  // Ringback tone for the caller (uploaded ringtone, looped) until the other
  // side answers (connected) or the call ends (overlay unmount). Both audio + video.
  useEffect(() => {
    if (!outgoing || connected) return;
    startRingback();
    return () => stopRingback();
  }, [outgoing, connected]);

  // Automatic quality ladder — crystal-clear on good networks, still usable on weak ones.
  // Tier 0 = best; higher index = lower bitrate/resolution/framerate.
  const TIERS = [
    { name: "hd",     vBitrate: 2500000, vScale: 1,   vFps: 30, aBitrate: 40000 },
    { name: "sd",     vBitrate: 1200000, vScale: 1.5, vFps: 30, aBitrate: 32000 },
    { name: "low",    vBitrate: 500000,  vScale: 2,   vFps: 20, aBitrate: 24000 },
    { name: "min",    vBitrate: 180000,  vScale: 3,   vFps: 15, aBitrate: 16000 },
  ];

  const applyQuality = async (idx) => {
    const pc = pcRef.current;
    if (!pc) return;
    const tier = TIERS[Math.max(0, Math.min(TIERS.length - 1, idx))];
    for (const sender of pc.getSenders()) {
      if (!sender.track) continue;
      const params = sender.getParameters();
      if (!params.encodings || !params.encodings.length) params.encodings = [{}];
      if (sender.track.kind === "video") {
        params.degradationPreference = "balanced";
        params.encodings[0].maxBitrate = tier.vBitrate;
        params.encodings[0].scaleResolutionDownBy = tier.vScale;
        params.encodings[0].maxFramerate = tier.vFps;
      } else {
        params.encodings[0].maxBitrate = tier.aBitrate;
      }
      try { await sender.setParameters(params); } catch (e) {}
    }
  };

  // Continuously measure the live connection (RTT, packet loss, available bandwidth)
  // and step the quality ladder up/down automatically — no manual switch.
  useEffect(() => {
    const poorRef = { n: 0 };
    const goodRef = { n: 0 };
    const lastBytes = { v: 0 };
    const stall = { n: 0 };
    const id = setInterval(async () => {
      const pc = pcRef.current;
      if (!pc || pc.connectionState !== "connected") return;
      try {
        const stats = await pc.getStats();
        let rtt = 0, loss = 0, availOut = 0, bytesIn = 0;
        stats.forEach((r) => {
          if (r.type === "candidate-pair" && r.nominated) {
            if (r.currentRoundTripTime != null) rtt = r.currentRoundTripTime;
            if (r.availableOutgoingBitrate != null) availOut = r.availableOutgoingBitrate;
          }
          if (r.type === "remote-inbound-rtp" && r.fractionLost != null) loss = Math.max(loss, r.fractionLost);
          if (r.type === "inbound-rtp" && r.bytesReceived != null) bytesIn += r.bytesReceived;
        });
        // Proactive media-stall detection: if inbound media stops flowing while the
        // connection still *claims* to be up, start reconnecting early (~5s) instead of
        // waiting for the browser's ~15s ICE timeout — then auto-resume when it recovers.
        if (connectedAtRef.current) {
          const grew = bytesIn > lastBytes.v;
          if (bytesIn > 0 && !grew) stall.n++; else stall.n = 0;
          lastBytes.v = bytesIn;
          if (stall.n >= 2 && !reconnectingRef.current) beginReconnect();
          else if (reconnectingRef.current && grew && navigator.onLine) { endReconnect(); setStatus("Connected"); }
        }
        const idx = qualityRef.current;
        const cur = TIERS[idx];
        const upTier = TIERS[Math.max(0, idx - 1)];
        // A sample is "poor" when latency/loss is high, or the estimated uplink
        // can't sustain the current tier; "good" when there's clear headroom to climb.
        const poorSample = loss > 0.08 || rtt > 0.5 || (availOut > 0 && availOut < cur.vBitrate * 0.8);
        const goodSample = loss < 0.03 && rtt < 0.3 && (availOut === 0 || availOut > upTier.vBitrate * 1.3);
        if (poorSample) { poorRef.n++; goodRef.n = 0; }
        else if (goodSample) { goodRef.n++; poorRef.n = 0; }
        else { poorRef.n = 0; goodRef.n = 0; }
        setPoorNet(idx >= 2 || poorRef.n >= 2);
        let want = idx;
        if (poorRef.n >= 2 && idx < TIERS.length - 1) want = idx + 1;   // downgrade fast (~5s) to keep the call alive
        else if (goodRef.n >= 3 && idx > 0) want = idx - 1;              // upgrade cautiously (~7.5s) to avoid flapping
        if (want !== idx) { qualityRef.current = want; applyQuality(want); poorRef.n = 0; goodRef.n = 0; }
      } catch (e) {}
    }, 2500);
    return () => clearInterval(id);
  }, []);

  // React instantly to the device losing/regaining its network (Wi-Fi↔cellular switch,
  // tunnel, airplane mode). Complements the byte-stall detector for real-world drops.
  useEffect(() => {
    const onOffline = () => { if (connectedAtRef.current) beginReconnect(); };
    const onOnline = () => { if (reconnectingRef.current && outgoing) tryReconnect(); };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => { window.removeEventListener("offline", onOffline); window.removeEventListener("online", onOnline); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ring + vibrate on incoming calls, continuously, until the user picks up,
  // declines, or the caller stops (overlay unmount clears everything). WhatsApp-style.
  useEffect(() => {
    if (!(incoming && !answered)) return;
    const canVibrate = typeof navigator !== "undefined" && "vibrate" in navigator;
    // WhatsApp-like pattern: buzz ~1s, pause ~1s, repeated.
    const buzz = () => { if (canVibrate) { try { navigator.vibrate([600, 300, 600, 900]); } catch (e) {} } };
    startCallRing(!!video);
    buzz();
    const vib = setInterval(buzz, 2400);
    return () => {
      stopCallRing();
      clearInterval(vib);
      if (canVibrate) { try { navigator.vibrate(0); } catch (e) {} }
    };
  }, [incoming, answered]);

  // Auto-dismiss an unanswered incoming call after ~45s → tell the caller and close.
  // Unmount cleanup logs it as a missed call (missed = incoming && !answered).
  useEffect(() => {
    if (!(incoming && !answered)) return;
    const to = setTimeout(() => {
      if (answeredRef.current) return;
      try { sendWs({ type: "rtc_end", to: peerId }); } catch (e) {}
      toast(t("call_missed") || `Missed call from ${peerName || ""}`.trim());
      onClose();
    }, 45000);
    return () => clearTimeout(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming, answered]);


  // Auto-hide call controls on video calls for a cleaner view; tap anywhere to reveal.
  const armHide = () => {
    clearTimeout(ctrlTimer.current);
    if (video && connected) ctrlTimer.current = setTimeout(() => setShowCtrl(false), 4000);
  };
  const revealCtrl = () => { setShowCtrl(true); armHide(); };
  useEffect(() => {
    if (video && connected) armHide(); else setShowCtrl(true);
    return () => clearTimeout(ctrlTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video, connected]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center" data-testid="call-overlay" onClick={video && connected ? revealCtrl : undefined}>
      <div className={`absolute inset-0 flex items-center justify-center ${fullscreen ? "z-20" : ""}`}>
        {video && <video ref={remoteRef} autoPlay playsInline muted data-testid="call-remote-video" className={fullscreen ? "absolute inset-0 w-full h-full object-cover" : "max-h-full max-w-full object-contain"} />}
      </div>
      {/* Remote audio is routed through this element so the loudspeaker/earpiece toggle can control the output sink. */}
      <audio ref={remoteAudioRef} autoPlay playsInline data-testid="call-remote-audio" className="hidden" />
      {micDenied && (
        <div className="absolute top-4 left-4 right-4 z-40 rounded-2xl border border-yellow-500/50 bg-yellow-500/15 px-4 py-3 text-sm text-yellow-100" data-testid="call-mic-permission-banner">
          <strong>Microphone is blocked.</strong>
          <p className="mt-1 text-yellow-100/90">Open <strong>Settings › Safari › Microphone</strong> (or the site settings) and choose <strong>Allow</strong>, then start the call again.</p>
        </div>
      )}
      {video && <video ref={localRef} autoPlay playsInline muted className={`absolute object-cover rounded-2xl border border-[var(--rc-gold)]/40 ${fullscreen ? "bottom-32 right-4 h-32 w-24 z-30" : "top-6 right-6 h-40 w-28"}`} />}

      {video && connected && (
        <div className={`absolute top-5 left-0 right-0 z-30 text-center pointer-events-none transition-opacity duration-300 ${showCtrl ? "opacity-100" : "opacity-0"}`}>
          <h2 className="font-heading text-lg text-white drop-shadow">{peerName}</h2>
          <p className="text-[var(--rc-gold)] text-xs drop-shadow">{status}</p>
        </div>
      )}

      {!(video && connected) && (
      <div className="relative text-center z-10">
        <div className="h-24 w-24 mx-auto rounded-3xl grid place-items-center text-[var(--rc-gold)] text-3xl font-heading mb-4 rc-ring border-2 border-[var(--rc-gold)]" style={{ backgroundColor: avatarColor(peerName) }}>
          {getInitials(peerName)}
        </div>
        <h2 className="font-heading text-3xl text-white mb-1">{peerName}</h2>
        <p className="text-[var(--rc-gold)]">{status}</p>
        {reconnecting && (
          <p className="mt-1 text-xs text-amber-300 animate-pulse" data-testid="reconnecting-notice">{t("call_reconnecting")}</p>
        )}
        {poorNet && status === "Connected" && (
          <p className="mt-1 text-xs text-amber-300" data-testid="poor-net-notice">{t("poor_connection_quality")}</p>
        )}
      </div>
      )}

      <div className={`relative z-30 mt-12 flex flex-wrap items-end justify-center gap-x-5 gap-y-4 px-4 max-w-md rc-safe-bottom transition-opacity duration-300 ${showCtrl ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        {incoming && !answered ? (
          <>
            <div className="flex flex-col items-center gap-2">
              <button onClick={() => { sendWs({ type: "rtc_reject", to: peerId }); onClose(); }} data-testid="reject-call-btn" className="h-16 w-16 grid place-items-center rounded-full bg-red-500 text-white"><PhoneOff size={26} /></button>
              <span className="text-xs text-white/80">{t("call_lbl_decline")}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button onClick={answer} data-testid="answer-call-btn" className="h-16 w-16 grid place-items-center rounded-full bg-emerald-500 text-white rc-ring"><Phone size={26} /></button>
              <span className="text-xs text-white/80">{t("call_lbl_answer")}</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center gap-2">
              <button onClick={toggleMic} className={`h-14 w-14 grid place-items-center rounded-full ${micOn ? "bg-white/10 text-white" : "bg-white text-black"}`} data-testid="call-mic-btn">{micOn ? <Mic size={22} /> : <MicOff size={22} />}</button>
              <span className="text-xs text-white/80">{micOn ? t("call_lbl_mute") : t("call_lbl_unmute")}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button onClick={() => setSpeakerOn((s) => !s)} className={`h-14 w-14 grid place-items-center rounded-full ${speakerOn ? "bg-[var(--rc-gold)] text-black" : "bg-white/10 text-white"}`} data-testid="call-speaker-btn">{speakerOn ? <Volume2 size={22} /> : <Volume1 size={22} />}</button>
              <span className="text-xs text-white/80">{hasHeadset ? t("call_lbl_headset") : t("call_lbl_speaker")}</span>
            </div>
            {video && (
              <div className="flex flex-col items-center gap-2">
                <button onClick={toggleCam} className="h-14 w-14 grid place-items-center rounded-full bg-white/10 text-white" data-testid="call-cam-btn">{camOn ? <Video size={22} /> : <VideoOff size={22} />}</button>
                <span className="text-xs text-white/80">{t("call_lbl_camera")}</span>
              </div>
            )}
            {video && (
              <div className="flex flex-col items-center gap-2">
                <button onClick={switchCamera} className="h-14 w-14 grid place-items-center rounded-full bg-white/10 text-white" data-testid="call-switch-btn"><SwitchCamera size={22} /></button>
                <span className="text-xs text-white/80">{t("call_lbl_flip")}</span>
              </div>
            )}
            {video && (
              <div className="flex flex-col items-center gap-2">
                <button onClick={() => setFullscreen((f) => !f)} className={`h-14 w-14 grid place-items-center rounded-full ${fullscreen ? "bg-[var(--rc-gold)] text-black" : "bg-white/10 text-white"}`} data-testid="call-fullscreen-btn">{fullscreen ? <Minimize2 size={22} /> : <Maximize2 size={22} />}</button>
                <span className="text-xs text-white/80">{fullscreen ? t("call_lbl_normal") : t("call_lbl_fullscreen")}</span>
              </div>
            )}
            <div className="flex flex-col items-center gap-2">
              <button onClick={hangup} className="h-16 w-16 grid place-items-center rounded-full bg-red-500 text-white" data-testid="hangup-btn"><PhoneOff size={26} /></button>
              <span className="text-xs text-white/80">{t("call_lbl_end")}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
